// src/components/fnct/EtatsFnct.tsx — SCE Issues
// États d'une action et d'une FNC-T, dans le langage de `ui/EtatProbleme` :
// noir et gris, une seule couleur vive par famille — le rouge pour le retard,
// le vert pour ce qui est terminé. Libellés en français (l'interface), le
// rapport client dira la même chose en anglais (LIBELLES_*.en).

import type { CSSProperties } from 'react'
import { AlertCircle, CalendarClock, Check, CheckCircle2, CircleDashed, Clock, Hourglass } from 'lucide-react'
import { Infobulle } from '@/components/ui/Infobulle'
import { LIBELLE_ATTENTE_BE } from '@/utils/attenteBE'
import { LIBELLES_STATUT_ACTION, type CriticiteRpn, type StatutAction } from '@/types/fnct.types'
import type { FiltreEtatFnct } from '@/utils/listeFnct'
import type { EtapeFnct } from '@/utils/ficheFnct'
import type { EtatLigneFnct } from '@/utils/tableauFnct'

const ICONES: Record<StatutAction, { Icone: typeof Clock; couleur: string }> = {
  'a-planifier': { Icone: CircleDashed, couleur: 'var(--color-gray-50)' },
  'en-cours': { Icone: Clock, couleur: 'var(--color-black)' },
  reportee: { Icone: CalendarClock, couleur: 'var(--color-black)' },
  'en-retard': { Icone: AlertCircle, couleur: 'var(--color-traffic-red)' },
  'terminee-a-temps': { Icone: CheckCircle2, couleur: 'var(--color-traffic-green)' },
  'terminee-en-retard': { Icone: CheckCircle2, couleur: 'var(--color-traffic-green)' },
}

/**
 * « En attente du BE » (utils/attenteBE, 23/10) : sous l'état d'une action qui
 * attend la mise à jour du P&ID / de la PPL, de l'EDE ou de l'HDS. Elle reste en
 * retard si son échéance est passée ; ceci dit pourquoi. Gris, jamais rouge.
 * `sombre` : sur le bandeau noir de « Mes FNC-T ».
 */
export function MentionAttenteBE({ sombre = false, style }: { sombre?: boolean; style?: CSSProperties }): JSX.Element {
  return (
    <span
      className="inline-flex items-center gap-1"
      title="L’action du BE (mise à jour du document) n’est pas encore close : celle-ci en dépend."
      style={{ fontSize: 12, lineHeight: '16px', fontWeight: 400, color: sombre ? 'var(--color-gray-30)' : 'var(--color-gray-70)', whiteSpace: 'nowrap', ...style }}
    >
      <Hourglass size={12} aria-hidden="true" />
      {LIBELLE_ATTENTE_BE}
    </span>
  )
}

/** Icône + mot : « En retard », « Terminée à temps »… */
export function StatutActionLibelle({ statut }: { statut: StatutAction }): JSX.Element {
  const { Icone, couleur } = ICONES[statut]
  return (
    <span
      className="inline-flex items-center gap-1.5"
      style={{
        fontSize: 'var(--font-size-info)',
        whiteSpace: 'nowrap',
        color: statut === 'en-retard' ? 'var(--color-traffic-red)' : statut === 'a-planifier' ? 'var(--color-gray-70)' : 'var(--color-text-primary)',
        fontWeight: statut === 'en-retard' ? 600 : 400,
      }}
    >
      <Icone size={15} color={couleur} strokeWidth={2} aria-hidden="true" />
      {LIBELLES_STATUT_ACTION[statut].fr}
    </span>
  )
}

const ETATS_FNCT: Record<Exclude<FiltreEtatFnct, 'toutes'>, { Icone: typeof Clock; couleur: string; libelle: string }> = {
  ouvertes: { Icone: Clock, couleur: 'var(--color-black)', libelle: 'Ouverte' },
  // Pas encore ouverte : le cercle en pointillés des problèmes « à qualifier ».
  releve: { Icone: CircleDashed, couleur: 'var(--color-black)', libelle: 'En relevé' },
  closes: { Icone: CheckCircle2, couleur: 'var(--color-traffic-green)', libelle: 'Close' },
}

/** État d'une FNC-T : horloge (ouverte), pointillés (en relevé) ou coche verte (close), durée en clair. */
export function EtatFnctLibelle({ etat, jours }: { etat: Exclude<FiltreEtatFnct, 'toutes'>; jours: number | null }): JSX.Element {
  const duree = jours === null ? '' : ` · ${jours} j`
  const { Icone, couleur, libelle } = ETATS_FNCT[etat]
  return (
    <span className="inline-flex items-center gap-1.5" style={{ fontSize: 'var(--font-size-info)', whiteSpace: 'nowrap' }}>
      <Icone size={15} color={couleur} aria-hidden="true" />
      {libelle}
      <span style={{ color: 'var(--color-gray-70)' }}>{duree}</span>
    </span>
  )
}

/** Avancement des actions : « 2 / 3 », retard en rouge, détail au survol. */
export function AvancementActions({
  terminees,
  total,
  enRetard,
}: {
  terminees: number
  total: number
  enRetard: number
}): JSX.Element {
  if (total === 0) return <span style={{ color: 'var(--color-gray-70)', fontSize: 'var(--font-size-info)' }}>Aucune action</span>
  return (
    <Infobulle
      titre={`${terminees} action${terminees > 1 ? 's' : ''} terminée${terminees > 1 ? 's' : ''} sur ${total}`}
      detail={enRetard > 0 ? `${enRetard} en retard` : undefined}
    >
      <span className="inline-flex items-center gap-1.5" style={{ fontSize: 'var(--font-size-info)', whiteSpace: 'nowrap' }}>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {terminees} / {total}
        </span>
        {enRetard > 0 && (
          <span className="inline-flex items-center gap-1" style={{ color: 'var(--color-traffic-red)', fontWeight: 600 }}>
            <AlertCircle size={13} aria-hidden="true" />
            {enRetard}
          </span>
        )}
      </span>
    </Infobulle>
  )
}

/** Criticité du RPN : Critical en rouge, Major en noir, Minor en gris (comme les points des problèmes). */
export function CriticiteRpnLibelle({ criticite, rpn }: { criticite: CriticiteRpn; rpn: number }): JSX.Element {
  const couleur =
    criticite === 'Critical' ? 'var(--color-traffic-red)' : criticite === 'Major' ? 'var(--color-black)' : 'var(--color-gray-70)'
  return (
    <span style={{ color: couleur, fontWeight: criticite === 'Minor' ? 400 : 600 }}>
      {criticite} <span style={{ fontWeight: 400, color: 'var(--color-gray-70)' }}>· RPN {rpn}</span>
    </span>
  )
}

/** L'état d'une FNC-T dans une ligne : retard en rouge, prête à clore en gras, close en vert. */
export function EtatLigne({ etat, texte }: { etat: EtatLigneFnct; texte: string }): JSX.Element {
  const rouge = etat === 'retard'
  return (
    <span className="inline-flex items-center gap-1.5" style={{ fontSize: 14, whiteSpace: 'nowrap', color: rouge ? 'var(--color-traffic-red)' : 'var(--color-black)', fontWeight: rouge || etat === 'prete' ? 600 : 400 }}>
      {rouge && <AlertCircle size={15} aria-hidden="true" />}
      {etat === 'prete' && <CheckCircle2 size={15} aria-hidden="true" />}
      {etat === 'close' && <CheckCircle2 size={15} color="var(--color-traffic-green)" aria-hidden="true" />}
      {etat === 'encours' && <Clock size={15} aria-hidden="true" />}
      {/* Pas encore ouverte : les pointillés de la fiche et des problèmes « à qualifier », pas l'horloge (30/10). */}
      {etat === 'releve' && <CircleDashed size={15} aria-hidden="true" />}
      {texte}
    </span>
  )
}

const ETAT_ETAPE: Record<EtapeFnct['etat'], string> = { fait: 'faite', close: 'close', retard: 'en retard', encours: 'en cours', avenir: 'à venir', facultatif: 'facultative' }

/** Une étape de « Où en est-on » : noir fait, vert close, rouge en retard, cerclée en cours, grise à venir, pointillée facultative. */
export function PastilleEtape({ etat, taille = 18 }: { etat: EtapeFnct['etat']; taille?: number }): JSX.Element {
  const base: CSSProperties = { width: taille, height: taille, borderRadius: taille / 2, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }
  const coche = Math.round(taille * 0.6)
  if (etat === 'fait' || etat === 'close')
    return (
      <span style={{ ...base, backgroundColor: etat === 'fait' ? 'var(--color-black)' : 'var(--color-traffic-green)', color: 'var(--color-white)' }}>
        <Check size={coche} strokeWidth={3} />
      </span>
    )
  if (etat === 'retard') return <span style={{ ...base, backgroundColor: 'var(--color-traffic-red)', color: 'var(--color-white)', fontSize: Math.round(taille * 0.6), fontWeight: 700 }}>!</span>
  if (etat === 'encours')
    return (
      <span style={{ ...base, border: '2px solid var(--color-black)' }}>
        <span style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: 'var(--color-black)' }} />
      </span>
    )
  return <span style={{ ...base, border: `2px ${etat === 'facultatif' ? 'dashed var(--color-gray-40)' : 'solid var(--color-gray-30)'}` }} />
}

/** Les 4 étapes de « Où en est-on », en petit. Le détail se lit au survol et pour les lecteurs d'écran. */
export function RailEtapes({ etapes }: { etapes: readonly EtapeFnct[] }): JSX.Element {
  const resume = etapes.map((e) => `${e.titre} : ${ETAT_ETAPE[e.etat]}`).join(', ')
  return (
    <span role="img" aria-label={resume} title={resume} className="inline-flex items-center" style={{ flex: 'none' }}>
      {etapes.map((e, i) => {
        const prec = etapes[i - 1]
        const lien = prec && (prec.etat === 'fait' || prec.etat === 'close') ? 'var(--color-black)' : 'var(--color-gray-30)'
        return (
          <span key={e.titre} className="inline-flex items-center">
            {i > 0 && <span style={{ width: 10, height: 2, backgroundColor: lien }} />}
            <PastilleEtape etat={e.etat} />
          </span>
        )
      })}
    </span>
  )
}
